import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useLocation, useParams } from 'react-router-dom';
import { auth } from '../firebase/config';
import { createListing, getListing, updateListing, publishListing, unpublishListing, checkUrlExists } from '../services/listingService';
import { getCategories } from '../services/configService';
import UrlSubmitModal from '../components/UrlSubmitModal';
import LoadingSpinner from '../components/LoadingSpinner';
import ListingFormFields from '../components/ListingFormFields';
import UnpublishWarningModal from '../components/UnpublishWarningModal';
import OverrideConfirmationModal from '../components/OverrideConfirmationModal';
import { scrapeWebsite } from '../services/scraper';
import { validateEmail } from '../utils/validation';
import { formatUrl } from '../utils/urlUtils';
import { deleteListing } from '../services/listingService';
import { fetchKeywords } from '../api/externalApi';

const EditListing = ({ onSuccess }) => {
  const { id } = useParams();
  const location = useLocation();
  const { url, email } = location.state || {};
  const [loading, setLoading] = useState(true);
  const [initialScrapingDone, setInitialScrapingDone] = useState(false);
  const errorRef = useRef(null);

  const [formData, setFormData] = useState({
    name: '',
    description: '',
    url: url || '',
    email: email || '',
    category: '', // Initialize category as empty
    image: '',
    location: '',
    isUrlValid: false,  // Add this line
  });
  const [categories, setCategories] = useState([]);
  const [error, setError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showUrlModal, setShowUrlModal] = useState(!id && !url);
  const [isScrapingLoading, setIsScrapingLoading] = useState(false);
  const [isLoadingExisting, setIsLoadingExisting] = useState(!!id);
  const [isUrlValid, setIsUrlValid] = useState(false);
  const [hasChanges, setHasChanges] = useState(false);
  const [initialFormData, setInitialFormData] = useState(null);
  const [urlValid, setUrlValid] = useState(false);
  const [lastSavedAt, setLastSavedAt] = useState(null);
  const [lastPublishedAt, setLastPublishedAt] = useState(null);
  const [isSaving, setIsSaving] = useState(false);
  const [isPublishing, setIsPublishing] = useState(false);
  const [isUrlChanged, setIsUrlChanged] = useState(false);
  const [showUnpublishWarning, setShowUnpublishWarning] = useState(false);
  const [pendingSave, setPendingSave] = useState(null);
  const [abortController, setAbortController] = useState(null);
  const [showOverrideConfirmation, setShowOverrideConfirmation] = useState(false);
  const [pendingOverrides, setPendingOverrides] = useState(null);
  const [showDeleteConfirmation, setShowDeleteConfirmation] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    const loadData = async () => {
      try {
        // Load categories
        const cats = await getCategories();
        setCategories(cats);
        console.log('useEffect: Categories:', cats);

        // If editing existing listing
        if (id) {
          setIsLoadingExisting(true);
          console.log('Page load: id:', id);
          const listing = await getListing(id);
          
          // Ensure all fields are present with default values
          const initialData = {
            name: listing.name || '',
            description: listing.description || '',
            url: listing.url || '',
            email: listing.email || '',
            category: listing.category || '',
            isPublished: listing.isPublished || false,
            isUrlValid: listing.isUrlValid || false,  // Add this line
            image: listing.image || '',
            location: listing.location || '',
            // Do not include derived fields like keywords
          };
          
          setFormData(initialData);
          console.log('Page load: form data (existing):', initialData);
          
          // Store a deep copy to avoid reference issues
          setInitialFormData(JSON.parse(JSON.stringify(initialData)));
          console.log('Initial form data stored for comparison:', JSON.parse(JSON.stringify(initialData)));
          
          setLastSavedAt(listing.lastSavedAt?.toDate());
          setLastPublishedAt(listing.lastPublishedAt?.toDate());
          setUrlValid(listing.isUrlValid);
        }
        // If new listing with URL from profile completion
        else if (url) {
          setIsScrapingLoading(true);
          try {
            const scrapedData = await scrapeWebsite(url);
            if (scrapedData) {
              const newData = {
                name: scrapedData.title || '',
                description: scrapedData.description || '',
                url: scrapedData.url,
                email: scrapedData.email || '',
                category: scrapedData.category || '',
                image: scrapedData.image || '',
                location: scrapedData.location || ''
              };
              
              setFormData(newData);
              // Log the data right after setting it
              console.log('Page load: form data (scraped):', newData);
              
              setHasChanges(true);
              setUrlValid(true);
            }
          } catch (error) {
            console.error('First submission: Error during initial scraping:', error);
            setError('Unable to analyze website automatically. Please fill in the details manually.');
          }
        } else {
          // Log the initial empty form data for new listings without URL
          console.log('Page load: form data (new empty):', formData);
        }
      } catch (error) {
        console.error('Page load: Error loading data:', error);
        setError('Failed to load data');
      } finally {
        console.log('Page load: loading done');
        setIsScrapingLoading(false);
        setIsLoadingExisting(false);
        setLoading(false);
        setInitialScrapingDone(true);
      }
    };

    loadData();
  }, [id, url, email]); // Remove formData from dependencies to avoid loop

  // Add this effect to handle error scrolling
  useEffect(() => {
    if ((error || successMessage) && errorRef.current) {
      errorRef.current.scrollIntoView({
        behavior: 'smooth',
        block: 'center'
      });
    }
  }, [error, successMessage]);

  const handleUrlSubmit = async (url) => {
    console.log('handleUrlSubmit: Url submitted for analysis');
    setIsScrapingLoading(true);
    try {
      // Convert URL to lowercase
      url = url.toLowerCase();
      
      // Check if URL exists first
      const urlExists = await checkUrlExists(url);
      if (urlExists) {
        setError('This website URL already exists in another listing');
        setShowUrlModal(false);
        return;
      }

      let formattedUrl = formatUrl(url);

      const scrapedData = await scrapeWebsite(formattedUrl);
      console.log('handleUrlSubmit: Scraped data: ', scrapedData)
      if (scrapedData) {
        const newFormData = {
          ...formData,
          name: scrapedData.title || '',
          description: scrapedData.description || '',
          url: formattedUrl,
          email: scrapedData.email || '',
          image: scrapedData.image || '',
          location: scrapedData.location || '',
          category: scrapedData.category || '',
          isUrlValid: true
        };
        console.log('handleUrlSubmit: Scraped category:', scrapedData.category); // Add this line
        setFormData(newFormData);
        setIsUrlValid(true);
        setUrlValid(true);
        setIsUrlChanged(false);
        // Since this is always for new listings (modal only shows for new), always enable save
        setHasChanges(true);
      } else {
        console.log('Analysis failed: no data returned');
        setError('Unable to analyze website. Please check the URL or try again later.');
        setIsUrlValid(false);
        setUrlValid(false);
      }
    } catch (error) {
      console.error('handleUrlSubmit: Error scraping website:', error);
      console.log('handleUrlSubmit: Error scraping website:', error);
      setError('Unable to analyze website. Please check if the URL is correct.');
    } finally {
      console.log('handleUrlSubmit: Scraping is done!');
      setIsScrapingLoading(false);
      setShowUrlModal(false);
    }
  };

  const getOverridableFields = (scrapedData, currentData) => {
    const overrides = [];

    if (scrapedData.title && currentData.name && scrapedData.title !== currentData.name) {
      overrides.push({
        field: 'name',
        current: currentData.name,
        new: scrapedData.title
      });
    }

    if (scrapedData.description && currentData.description &&
      scrapedData.description !== currentData.description) {
      overrides.push({
        field: 'description',
        current: currentData.description,
        new: scrapedData.description
      });
    }

    if (scrapedData.email && currentData.email && scrapedData.email !== currentData.email) {
      overrides.push({
        field: 'email',
        current: currentData.email,
        new: scrapedData.email
      });
    }

    if (scrapedData.image && currentData.image && scrapedData.image !== currentData.image) {
      overrides.push({
        field: 'image',
        current: currentData.image,
        new: scrapedData.image
      });
    }

    if (scrapedData.location && currentData.location &&
      scrapedData.location !== currentData.location) {
      overrides.push({
        field: 'location',
        current: currentData.location,
        new: scrapedData.location
      });
    }

    if (scrapedData.category && currentData.category &&
      scrapedData.category !== currentData.category) {
      overrides.push({
        field: 'category',
        current: currentData.category,
        new: scrapedData.category
      });
    }

    return overrides;
  };

  const handleAnalyze = async () => {
    if (!formData.url) {
      setError('Please enter a URL to analyze');
      return;
    }

    let formattedUrl = formatUrl(formData.url.toLowerCase());

    try {
      new URL(formattedUrl); // Validate URL format
    } catch {
      setError('Please enter a valid URL');
      return;
    }

    setError('');
    setIsScrapingLoading(true);

    // Create new abort controller
    const controller = new AbortController();
    setAbortController(controller);

    try {
      // Check if URL exists first
      const urlExists = await checkUrlExists(formattedUrl, id); // Pass the current listing ID to exclude it
      if (urlExists) {
        setError('This website URL already exists in another listing');
        setIsScrapingLoading(false);
        return;
      }

      console.log('Starting analysis for:', formattedUrl);
      const scrapedData = await scrapeWebsite(formattedUrl, { // Use scrapeWebsite directly
        signal: controller.signal,
        timeout: 15000 // 15 seconds timeout
      });

      if (scrapedData) {
        console.log('Analysis successful:', scrapedData);
        console.log('handleAnalyze: Scraped category:', scrapedData.category); // Add this line

        // Check for potential overrides
        const overrides = getOverridableFields(scrapedData, formData);

        if (overrides.length > 0) {
          // Show confirmation dialog with overridable fields
          setPendingOverrides({
            overrides,
            scrapedData
          });
          setShowOverrideConfirmation(true);
          setIsUrlValid(true);
          setUrlValid(true);
          setIsUrlChanged(false);
          // For new listings, enable save immediately
          if (!id) {
            setHasChanges(true);
          }
        } else {
          // No overrides needed, update form directly
          const newFormData = {
            ...formData,
            name: scrapedData.title || formData.name,
            description: scrapedData.description || formData.description,
            email: scrapedData.email || formData.email,
            url: formattedUrl,
            image: scrapedData.image || formData.image,
            location: scrapedData.location || formData.location,
            category: scrapedData.category || formData.category
          };
          setFormData(newFormData);
          setIsUrlValid(true);
          setUrlValid(true);
          setIsUrlChanged(false);
          // For new listings, always enable save. For existing, only if changed
          setHasChanges(id ?
            JSON.stringify(newFormData) !== JSON.stringify(initialFormData) :
            true
          );
        }
      } else {
        console.log('Analysis failed: no data returned');
        setError('Unable to analyze website. Please check the URL or try again later.');
        setIsUrlValid(false);
        setUrlValid(false);
      }
    } catch (error) {
      console.error('Analysis error:', error);
      setError(error.message === 'Scraping timed out'
        ? 'Analysis took too long. Please try again.'
        : 'Failed to analyze website. Please check the URL or try again later.');
      setIsUrlValid(false);
      setUrlValid(false);
    } finally {
      setIsScrapingLoading(false);
      setAbortController(null);
    }
  };

  const handleOverrideConfirm = () => {
    const { scrapedData, overrides } = pendingOverrides;
    const updatedData = { ...formData };

    // Only update selected fields
    overrides.forEach(({ field }) => {
      switch (field) {
        case 'name':
          updatedData.name = scrapedData.title;
          break;
        case 'description':
          updatedData.description = scrapedData.description;
          break;
        case 'email':
          updatedData.email = scrapedData.email;
          break;
        case 'image':
          updatedData.image = scrapedData.image;
          break;
        case 'location':
          updatedData.location = scrapedData.location;
          break;
        case 'category':
          updatedData.category = scrapedData.category;
          break;
      }
    });

    setFormData(updatedData);
    setIsUrlValid(true);
    setUrlValid(true);
    setIsUrlChanged(false);
    setShowOverrideConfirmation(false);
    setPendingOverrides(null);
    // For new listings, always enable save. For existing, only if changed
    setHasChanges(id ?
      JSON.stringify(updatedData) !== JSON.stringify(initialFormData) :
      true
    );
  };

  const handleOverrideCancel = () => {
    // Just update URL validity since scraping was successful
    setIsUrlValid(true);
    setUrlValid(true);
    setIsUrlChanged(false);
    setShowOverrideConfirmation(false);
    setPendingOverrides(null);
    // Don't set hasChanges since user rejected changes
    setHasChanges(false);
  };

  const handleCancelAnalysis = () => {
    if (abortController) {
      console.log('Cancelling analysis...');
      abortController.abort();
      setIsScrapingLoading(false);
      setAbortController(null);
      setError('Analysis cancelled');
    }
  };

  const validateUrlSilently = async (url) => {
    try {
      const scrapedData = await scrapeWebsite(url);
      return !!scrapedData;
    } catch (error) {
      return false;
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // If URL was changed but not analyzed, validate silently
    if (isUrlChanged) {
      console.log('Validating changed URL silently...');
      const isValid = await validateUrlSilently(formData.url);
      setUrlValid(isValid);
      setIsUrlChanged(false);

      // Check if listing is currently published
      const currentlyPublished = initialFormData?.isPublished;
      console.log('Currently published:', currentlyPublished);

      // If URL is invalid and listing is published, show warning
      if (!isValid && currentlyPublished) {
        console.log('Showing unpublish warning...');
        setShowUnpublishWarning(true);
        setPendingSave(e);
        return;
      }
    }

    // Proceed with normal save
    await saveChanges();
  };

  const saveChanges = async (shouldUnpublish = false) => {
    setError('');
    setSuccessMessage('');
    setIsSaving(true);

    try {
      // Extract keywords from title and description
      let keywords = [];
      try {
        const content = `${formData.name} ${formData.description}`.trim();
        if (content) {
          const keywordsResult = await fetchKeywords(content, formData.url);
          keywords = keywordsResult.keywords;
        }
      } catch (keywordError) {
        console.error('Error extracting keywords:', keywordError);
        // Continue with save even if keyword extraction fails
      }

      if (id) {
        // Include isPublished status in the update data
        const updateData = {
          ...formData,
          keywords, // Add keywords to the data
          isPublished: shouldUnpublish ? false : formData.isPublished // Keep existing status unless unpublishing
        };

        const shouldValidateUrl = isUrlChanged && !urlValid;
        if (shouldUnpublish) {
          await unpublishListing(id);
        }
        const result = await updateListing(id, updateData, shouldValidateUrl);
        setUrlValid(result.isUrlValid);
        setLastSavedAt(result.lastSavedAt);

        // Update form data to reflect new published status
        setFormData(prev => ({
          ...prev,
          isPublished: shouldUnpublish ? false : prev.isPublished,
          keywords
        }));

        await onSuccess();
        setHasChanges(false);
        
        if (shouldUnpublish) {
          setError('Listing updated and unpublished due to invalid URL');
        } else {
          setSuccessMessage('Listing updated successfully');
        }
      } else {
        const newId = await createListing(
          { ...formData, keywords }, // Add keywords to the data
          auth.currentUser.uid,
          auth.currentUser.email
        );
        await onSuccess();
        navigate('/dashboard');
      }
    } catch (error) {
      setError(error.message);
      setSuccessMessage('');
    } finally {
      setIsSaving(false);
      setShowUnpublishWarning(false);
      setPendingSave(null);
    }
  };

  const handleUnpublishConfirm = async () => {
    await saveChanges(true);
  };

  const handleUnpublishCancel = () => {
    setShowUnpublishWarning(false);
    setPendingSave(null);
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    let newValue = value;

    // Convert URL to lowercase if the field is 'url'
    if (name === 'url') {
      newValue = value.toLowerCase();
      setIsUrlChanged(true);
      setUrlValid(false); // Invalidate URL when changed
    }
    
    // Clear error message if URL field is being changed
    if (name === 'url' && error && error.includes('analyze')) {
      setError('');
    }
    
    // Create new form data with the updated field
    const newFormData = { ...formData, [name]: newValue };
    setFormData(newFormData);
    
    // Add debugging to check the comparison
    console.log(`Field ${name} changed from "${formData[name]}" to "${newValue}"`);
    
    // Deep comparison might be causing issues when comparing objects
    // Let's do a more direct field-by-field comparison
    if (initialFormData) {
      // Check if any relevant field has changed
      const hasFieldChanges = Object.keys(initialFormData).some(key => {
        // Skip comparison for keywords or other derived properties
        if (key === 'keywords' || key === 'isPublished') return false;
        
        const initialValue = initialFormData[key] || '';
        const currentValue = newFormData[key] || '';
        
        // Log the comparison for debugging
        if (key === name) {
          console.log(`Comparing ${key}: "${initialValue}" vs "${currentValue}" => ${initialValue !== currentValue}`);
        }
        
        return initialValue !== currentValue;
      });
      
      console.log('Has changes after field-by-field comparison:', hasFieldChanges);
      setHasChanges(hasFieldChanges);
    } else {
      // For new listings, always enable 
      setHasChanges(true);
    }
  };

  const handleEmailValidation = (e) => {
    const { value } = e.target;
    if (value && !validateEmail(value)) {
      setError('Please enter a valid email address');
    } else {
      setError('');
    }
  };

  const handlePublish = async () => {
    if (!urlValid) {
      setError('A valid URL is required to publish the listing');
      return;
    }

    // if (!formData.email || !validateEmail(formData.email)) {
    //   setError('A valid email address is required to publish the listing');
    //   return;
    // }

    setIsPublishing(true);
    try {
      console.log('Publishing flow - Starting with ID:', id);
      
      // Extract keywords from title and description
      let keywords = [];
      try {
        const content = `${formData.name} ${formData.description}`.trim();
        if (content) {
          const keywordsResult = await fetchKeywords(content, formData.url);
          keywords = keywordsResult.keywords;
        }
      } catch (keywordError) {
        console.error('Error extracting keywords:', keywordError);
        // Continue with publish even if keyword extraction fails
      }

      // set the isUrlValid to true in the formData
      const newFormData = {
        ...formData,
        isUrlValid: true
      };
      
      // First the form data with keywords
      const shouldValidateUrl = isUrlChanged && !urlValid;
      const updateResult = await updateListing(id, { 
        ...newFormData,
        keywords
      }, shouldValidateUrl);
      console.log('Publishing flow - Save completed:', updateResult);
      setUrlValid(updateResult.isUrlValid);
      setLastSavedAt(updateResult.lastSavedAt);
      setHasChanges(false);
      
      // Update form data to include keywords
      setFormData(prev => ({
        ...prev,
        keywords
      }));

      // Small delay to ensure save is complete
      // await new Promise(resolve => setTimeout(resolve, 500));

      // Verify we have a valid ID before publishing
      const listingId = updateResult.id || id;
      console.log('Publishing flow - About to publish with ID:', listingId);

      if (!listingId) {
        throw new Error('No valid listing ID found for publishing');
      }

      // Then publish the listing
      const publishResult = await publishListing(listingId);
      console.log('Publishing flow - Publish completed:', publishResult);
      setLastPublishedAt(publishResult.lastPublishedAt);

      await onSuccess();
      navigate('/dashboard');
    } catch (error) {
      console.error('Publishing flow - Error:', error);
      setError(error.message);
    } finally {
      setIsPublishing(false);
    }
  };

  const handleDelete = () => {
    setShowDeleteConfirmation(true);
  };

  const handleConfirmDelete = async () => {
    try {
      await deleteListing(id);
      await onSuccess();
      navigate('/dashboard');
    } catch (error) {
      setError(error.message);
    } finally {
      setShowDeleteConfirmation(false);
    }
  };

  const handleCancelDelete = () => {
    setShowDeleteConfirmation(false);
  };

  // Update conditions for save and publish buttons
  const canSave = urlValid && (id ? hasChanges : true);

  const canPublish = urlValid && !isUrlChanged && (
    formData.name &&
    formData.description &&
    formData.category &&
    !formData.isPublished && // Add this condition to check if listing is unpublished
    (!lastPublishedAt || (lastSavedAt && lastSavedAt > lastPublishedAt))
  );

  // Get publish button label based on state
  const getPublishButtonLabel = () => {
    if (isPublishing) return 'Publishing...';
    if (canSave) return 'Save & publish';
    return 'Publish';
  };

  // Show different loading messages based on the type of loading
  if (loading || !initialScrapingDone) {
    return <LoadingSpinner message={isLoadingExisting ? "Loading..." : "Analyzing website..."} />;
  }

  return (
    <>
      {showUrlModal && (
        <UrlSubmitModal
          onSubmit={handleUrlSubmit}
          onClose={() => setShowUrlModal(false)}
        />
      )}

      {isScrapingLoading && (
        <LoadingSpinner
          message="Analyzing website..."
          onCancel={handleCancelAnalysis}
          showCancel={true}
        />
      )}

      {showUnpublishWarning && (
        <UnpublishWarningModal
          handleUnpublishCancel={handleUnpublishCancel}
          handleUnpublishConfirm={handleUnpublishConfirm}
        />
      )}

      {showOverrideConfirmation && (
        <OverrideConfirmationModal
          pendingOverrides={pendingOverrides}
          handleOverrideCancel={handleOverrideCancel}
          handleOverrideConfirm={handleOverrideConfirm}
        />
      )}

      {showDeleteConfirmation && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white p-6 rounded-lg max-w-md">
            <h3 className="text-xl font-bold mb-4">Confirm Delete</h3>
            <p className="text-gray-600 mb-6">
              Are you sure you want to delete this listing? This action cannot be undone.
            </p>
            <div className="flex gap-4 justify-end">
              <button
                onClick={handleCancelDelete}
                className="px-4 py-2 text-gray-600 hover:text-gray-800"
              >
                Cancel
              </button>
              <button
                onClick={handleConfirmDelete}
                className="px-4 py-2 bg-red-600 text-white rounded hover:bg-red-700"
              >
                Delete
              </button>
            </div>
          </div>
        </div>
      )}

      <div className="max-w-2xl mx-auto mt-10 p-6 bg-white rounded-lg shadow-md">
        <div className="flex justify-between items-center mb-6">
          <h2 className="text-2xl font-bold">
            {id ? 'Edit Listing' : 'Create Listing'}
          </h2>
          {id && (
            <button
              type="button"
              onClick={handleDelete}
              className="text-red-600 hover:text-white hover:bg-red-600 rounded-lg border border-red-600 transition-colors w-8 h-8 flex items-center justify-center"
              title="Delete listing"
            >
              <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
              </svg>
            </button>
          )}
        </div>

        {error && (
          <div
            ref={errorRef}
            className="mb-4 bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded"
          >
            {error}
          </div>
        )}
        
        {successMessage && (
          <div
            ref={errorRef}
            className="mb-4 bg-green-100 border border-green-400 text-green-700 px-4 py-3 rounded"
          >
            {successMessage}
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-gray-700 mb-2">Website URL</label>
            <div className="flex gap-2">
              <input
                type="url"
                name="url"
                className="flex-1 px-3 py-2 border rounded-lg"
                value={formData.url}
                onChange={handleChange}
                required
              />
              <button
                type="button"
                onClick={handleAnalyze}
                className="px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700 disabled:bg-gray-300"
                disabled={isScrapingLoading}
              >
                {isScrapingLoading ? 'Analyzing...' : 'Analyze'}
              </button>
            </div>
          </div>

          <ListingFormFields
            formData={formData}
            categories={categories}
            handleChange={handleChange}
            // handleEmailValidation={handleEmailValidation}
          />

          <div className="flex gap-2">
            <button
              type="submit"
              className="flex-1 bg-red-600 text-white py-2 rounded-lg hover:bg-red-700 disabled:bg-red-300"
              disabled={isSaving || isPublishing || !canSave}
            >
              {isSaving ? 'Saving...' : 'Save'}
            </button>
            <button
              type="button"
              onClick={handlePublish}
              className="flex-1 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:bg-green-300"
              disabled={isSaving || isPublishing || !canPublish}
            >
              {getPublishButtonLabel()}
            </button>
          </div>

          {(error || successMessage) && (
            <div className="text-center text-sm">
              {error && <p className="text-red-600">{error}</p>}
              {successMessage && <p className="text-green-600">{successMessage}</p>}
            </div>
          )}

          {/* {id && (
            <div className="mt-4">
              <button
                type="button"
                onClick={handleDelete}
                className="w-full px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700"
              >
                Delete Listing
              </button>
            </div>
          )} */}

        </form>
      </div>
    </>
  );
};

export default EditListing;

